import {TypedEventTarget} from './TypedEventTarget';
import {onLoadedDeferred} from './lib';
import {hel_DispatchEvent} from './moduleCaller';

import type {UserType} from '../../../HeliportJS/dist/models/auth';
import type {Return} from '../../../HeliportJS/dist/models/return';

let status: 'notInitialized' | 'initialized' | 'processing' = 'notInitialized';
/**
 * ログイン状態が変化したときに変化する非HttpOnly Cookieの前回の値。
 *
 * 未初期化時、およびCookieによる検出が利用できない (Vket SSOとAPEXドメインが異なる) 場合は `null`。
 * @see https://hikky.atlassian.net/wiki/spaces/VKETCLOUD/pages/1611661345
 */
let previousLoginStateChangeCookie: string | null = null;
/**
 * ログイン時、同一ユーザーの場合は同一となる値。
 *
 * 未初期化時、および非ログイン時 (ログアウト / ゲストユーザー) の場合は `null`。
 */
let previousLoginStateLogginedUserId: string | number | null = null;

export const loginStateChangeEmitter = new TypedEventTarget<{
  change: Event;
}>();

let waitingOnLoaded = false;

loginStateChangeEmitter.addEventListener('change', async function () {
  if (waitingOnLoaded) {
    return;
  }
  waitingOnLoaded = true;
  await onLoadedDeferred.promise;
  waitingOnLoaded = false;

  hel_DispatchEvent('login-state-change', JSON.stringify(null));
});

const fetchUserType = async (): Promise<UserType> => {
  const result = (await heliport.v3.api.auths.fetchUserType()) as Return<UserType>;
  if (result.status !== 'ok') {
    status = 'notInitialized';
    console.error(result);
    throw new Error(JSON.stringify(result));
  }
  return result.data;
};

export const fireEventIfLoginStateChanged = async (): Promise<void> => {
  if (status === 'processing') {
    return;
  }

  if (status === 'notInitialized') {
    status = 'processing';

    const userType = await fetchUserType();

    const loginStateChangeCookieReturn =
      globalThis.heliport.v3.api.auths.getLoginStateChangeCookie();
    if (loginStateChangeCookieReturn.status === 'ok') {
      // Cookieによる検出が利用できる場合
      previousLoginStateChangeCookie = loginStateChangeCookieReturn.data;
    }
    previousLoginStateLogginedUserId = userType.user?.id ?? null;

    // 初回は必ず発火
    loginStateChangeEmitter.dispatchEvent(new Event('change'));
  } else {
    status = 'processing';

    const loginStateChangeCookieReturn =
      globalThis.heliport.v3.api.auths.getLoginStateChangeCookie();
    if (
      loginStateChangeCookieReturn.status !== 'ok' ||
      loginStateChangeCookieReturn.data !== previousLoginStateChangeCookie
    ) {
      // Cookieによる検出が利用できない場合、もしくはCookie値が変化していれば
      const userType = await fetchUserType();
      const loginStateChangeCookieReturn =
        globalThis.heliport.v3.api.auths.getLoginStateChangeCookie();
      previousLoginStateChangeCookie =
        loginStateChangeCookieReturn.status === 'ok' ? loginStateChangeCookieReturn.data : null;
      const id = userType.user?.id ?? null;
      if (id !== previousLoginStateLogginedUserId) {
        // ログイン状態が変化していれば
        // heliport.jsが保持する権限JWTを破棄
        globalThis.heliport.v3.api.auths.refreshLoginState();
        // 発火
        loginStateChangeEmitter.dispatchEvent(new Event('change'));
        previousLoginStateLogginedUserId = id;
      }
    }
  }

  status = 'initialized';
};

export const hel_net_is_logged_in = (): boolean => {
  return previousLoginStateLogginedUserId !== null;
};
